import * as datapoints from '../logging/datapoints';
import { getFileItemsFromCache } from 'owa-groups-services';
import { getFileItemsPromiseOverride } from 'owa-groups-adaptors';
import { isSuccess } from 'owa-groups-services';
import BaseMailboxFileItem from 'owa-service/lib/contract/BaseMailboxFileItem';
import GetFilesResponseType from 'owa-service/lib/contract/GetFilesResponseType';
import updateRecentFilesStore from 'owa-group-files-hub-store/lib/actions/updateRecentFilesStore';
import updateRecentFilesLoadingStatus from 'owa-group-files-hub-store/lib/actions/updateRecentFilesLoadingStatus';
import { getRecentFilesList } from 'owa-group-files-hub-store/lib/selectors/getRecentFilesList';
import { clearFilesPreviewInformationAction } from 'owa-group-files-hub-store/lib/actions/clearFilesPreviewInformationAction';
import FileListLoadingStatus from 'owa-group-files-hub-store/lib/Schema/FileListLoadingStatus';
import GetFileItemsCacheStatus from 'owa-service/lib/contract/GetFileItemsCacheStatus';
import {
    FileInformationConverterForGetFileItems,
    MaxFiles,
} from '../utils/FileInformationConverterForGetFileItems';
import { PerformanceDatapoint, DatapointStatus } from 'owa-analytics';
import prefetchFilePreviewInformationAction from '../actions/PrefetchFilePreviewInformationAction';
import {
    isServiceFetchError,
    logResponseDataForOwaServerCalls,
    logErrorForEmptyOrNullResponse,
} from 'owa-server-call-logging/lib/owaServerCallLogging';
import { getErrorFromObject } from '../logging/genericInformationLogging';
import { getGroupMailboxExternalId } from 'owa-groups-shared-store';

function parseGetFileItemsResponse(
    response: any,
    datapoint: PerformanceDatapoint,
    groupSmtpAddress: string
) {
    if (datapoint) {
        datapoint.addCheckmark('ParsingResponse');
    }

    const recentFilesList = getRecentFilesList(groupSmtpAddress);

    if (response && response.Body && isSuccess(response.Body)) {
        const fileList: BaseMailboxFileItem[] = response.Body.FileItems;
        const responseStatus: GetFilesResponseType = response.Body.ResponseStatusType;
        const cacheStatus: GetFileItemsCacheStatus = response.Body.CacheStatus;

        // we want to continue showing the loading indicator if the below conditions are not satisfied. so do nothing.
        // If it is still in loading status, or refresh call has already failed then we proceed to update the recent files store with the parsed response,
        // else, if the refresh call has already finished successfully, then we want to skip this part
        if (
            cacheStatus != null &&
            cacheStatus.IsRetrievedFromCache &&
            responseStatus == GetFilesResponseType.Success &&
            fileList != null &&
            fileList.length > 0 &&
            (recentFilesList.fileListLoadingStatus == FileListLoadingStatus.Loading ||
                recentFilesList.fileListLoadingStatus == FileListLoadingStatus.Failed)
        ) {
            // If cache retrieved time is invalid, we do not know how old is the cached information. Set it to be null.
            const fileRetrievedTime = cacheStatus.Retrieved
                ? new Date(cacheStatus.Retrieved)
                : null;
            recentFilesList.fileItems = new FileInformationConverterForGetFileItems(
                fileList,
                groupSmtpAddress,
                fileRetrievedTime
            ).getFileInformation();
            recentFilesList.filesHash = cacheStatus.Hash;
            recentFilesList.fileListLoadingStatus = FileListLoadingStatus.Success;

            // Update recent files list map
            updateRecentFilesStore(groupSmtpAddress, recentFilesList);

            // prefetch file preview information asynchronously
            prefetchFilePreviewInformationAction(recentFilesList.fileItems);
        }
    } else {
        if (datapoint) {
            logErrorForEmptyOrNullResponse(datapoint, response);
            datapoint.endWithError(
                DatapointStatus.ServerError,
                new Error(
                    'Error response from server encountered while trying to read the response'
                )
            );
        }
    }
}

export function getRecentFilesFromCache(groupSmtpAddress: string) {
    const datapoint = new PerformanceDatapoint(
        datapoints.Datapoints.getFileItemsFromCacheAction.datapoint.name
    );

    datapoint.addCustomProperty('groupExternalId', getGroupMailboxExternalId(groupSmtpAddress));

    // clear the PreviewInformation Store
    clearFilesPreviewInformationAction();

    if (getFileItemsPromiseOverride) {
        getFileItemsPromiseOverride(groupSmtpAddress)
            .then((response: any) => {
                parseGetFileItemsResponse(response, datapoint, groupSmtpAddress);
                datapoint.end();
            })
            .catch(error => {
                const recentFilesList = getRecentFilesList(groupSmtpAddress);
                if (recentFilesList.fileListLoadingStatus == FileListLoadingStatus.Loading) {
                    updateRecentFilesLoadingStatus(groupSmtpAddress, FileListLoadingStatus.Failed);
                }

                datapoint.endWithError(DatapointStatus.ServerError, getErrorFromObject(error));
            });
    } else {
        getFileItemsFromCache(groupSmtpAddress, MaxFiles)
            .then((response: any) => {
                parseGetFileItemsResponse(response, datapoint, groupSmtpAddress);
                datapoint.end();
            })
            .catch(error => {
                if (isServiceFetchError(error)) {
                    logResponseDataForOwaServerCalls(datapoint, error.response);
                }

                const recentFilesList = getRecentFilesList(groupSmtpAddress);
                if (recentFilesList.fileListLoadingStatus == FileListLoadingStatus.Loading) {
                    updateRecentFilesLoadingStatus(groupSmtpAddress, FileListLoadingStatus.Failed);
                }

                datapoint.endWithError(DatapointStatus.ServerError, getErrorFromObject(error));
            });
    }
}
